import Accordion from "../../../components/accordian"
import { useTranslation } from "react-i18next";
import { useCallback, useState } from "react";
import FileUploadInput from "../../../components/inputs/FileUploadInput";
import { allowedFileTypes, allowedImagesTypes, ContentType, ISSUANCE_RENEWAL_ID_ATTACHMENT_KEYS, ISSUANCE_RENEWAL_ID_ATTACHMENT_TYPE, PARKING_STICKER_REQUEST_ATTACHMENT_KEYS, PARKING_STICKER_REQUEST_ATTACHMENTS_TYPE, USER_PROFILE_ATTACHMENTS_TYPE } from "../../../constants/common";
import { Control, FieldErrors, UseFormClearErrors } from "react-hook-form";
import toast from "react-hot-toast";
import { useGetIssuanceRenewalIdForStaffFromContractedCompaniesFileByType, useGetParkingEntryStickerFileByType } from "../../../hooks/permit/mutation";
import ViewAttachmentModal from "../../../components/modal/ViewAttachmentModal";

interface AttachmentsProps {
    isAttachments: {
        havePersonalPhoto: number;
        haveNationalIdCopy: number;
        haveContractorsAuthorizationLetter: number;
    };
    control: Control<{
        uploadPersonalPhoto?: any;
        uploadCopyOfNationalId?: any;
        uploadContractorsAuthorizationLetter?: any;

        contractorEmployeeName: string;
        nationality: string;
        nationalId: string;
        nationalIdExpiryDate: string;
        dob: string;
        contractorsCompanyName: string;
        department: string;
        jobPosition: string;
        workStartDate: string;
        residentialAddress: string;
        contactNumber: string;
        cardValidityFrom: string;
        cardValidityUntil: string;
    }, any>
    errors: FieldErrors<{
        uploadPersonalPhoto?: any;
        uploadCopyOfNationalId?: any;
        uploadContractorsAuthorizationLetter?: any;

        contractorEmployeeName: string;
        nationality: string;
        nationalId: string;
        nationalIdExpiryDate: string;
        dob: string;
        contractorsCompanyName: string;
        department: string;
        jobPosition: string;
        workStartDate: string;
        residentialAddress: string;
        contactNumber: string;
        cardValidityFrom: string;
        cardValidityUntil: string;
    }>
    clearErrors: UseFormClearErrors<{
        uploadPersonalPhoto?: any;
        uploadCopyOfNationalId?: any;
        uploadContractorsAuthorizationLetter?: any;
        contractorEmployeeName: string;
        nationality: string;
        nationalId: string;
        nationalIdExpiryDate: string;
        dob: string;
        contractorsCompanyName: string;
        department: string;
        jobPosition: string;
        workStartDate: string;
        residentialAddress: string;
        contactNumber: string;
        cardValidityFrom: string;
        cardValidityUntil: string;
    }>
    uploadRefs: Record<string, React.MutableRefObject<HTMLInputElement | null>>;
    fileNames: Record<string, string>;
    setFileName: (key: string, value: React.SetStateAction<string>) => void
    isFieldsDisabled: boolean;
    issuanceRenewalIdDetails?: {
        id: number;
    }
}

const Attachments = ({
    isAttachments,
    isFieldsDisabled,
    control,
    errors,
    uploadRefs,
    fileNames,
    setFileName,
    issuanceRenewalIdDetails,
}: AttachmentsProps) => {
    const { t } = useTranslation();

    const [selectedKey, setSelectedKey] = useState<string>("1");

    const [previewFiles, setPreviewFiles] = useState<
        Record<number, { url: string; contentType: string }>
    >({});
    const [imageSrc, setImageSrc] = useState("");
    const [contentType, setContentType] = useState("");
    const [isViewAttachmentModalOpen, setIsViewAttachmentModalOpen] =
        useState(false);
    const [selectedType, setSelectedType] = useState<number>(0);
    const [isGetFileByTypeLoading, setIsGetFileByTypeLoading] = useState(false);

    const {
        mutateAsync: GetIssuanceRenewalIdForStaffFromContractedCompaniesFileByType,
        isPending: isGetIssuanceRenewalIdForStaffFromContractedCompaniesFileByTypeLoading
    } = useGetIssuanceRenewalIdForStaffFromContractedCompaniesFileByType();

    const handleAttachmentView = useCallback(
        async (type: number) => {
            try {
                // If we already have a local preview for this attachment type
                if (previewFiles && previewFiles[type]) {
                    setImageSrc(previewFiles[type].url);
                    setContentType(previewFiles[type].contentType);
                    setIsViewAttachmentModalOpen(true);
                    return;
                }

                if (issuanceRenewalIdDetails?.id) {
                    setSelectedType(type);

                    const body = {
                        Id: issuanceRenewalIdDetails?.id,
                        Type: type,
                    };

                    const res = await GetIssuanceRenewalIdForStaffFromContractedCompaniesFileByType(body);

                    setIsGetFileByTypeLoading(isGetIssuanceRenewalIdForStaffFromContractedCompaniesFileByTypeLoading);

                    if (res) {
                        let url = "";
                        if (res?.contentType !== ContentType.PDF) {
                            url = `data:${res?.contentType};base64,${res?.photoFile}`;
                        } else {
                            const binaryString = atob(res?.photoFile);
                            const byteArray = new Uint8Array(binaryString.length);

                            for (let i = 0; i < binaryString.length; i++) {
                                byteArray[i] = binaryString.charCodeAt(i);
                            }

                            const blob = new Blob([byteArray], { type: ContentType.PDF });
                            url = URL.createObjectURL(blob);
                        }
                        setImageSrc(url);
                        setContentType(res?.contentType);
                        setIsViewAttachmentModalOpen(true);
                    }

                    return;
                }
            } catch (error) {
                toast.error("Something went wrong");
            } finally {
                setSelectedType(0);
            }
        },
        [
            previewFiles,
            issuanceRenewalIdDetails,
            GetIssuanceRenewalIdForStaffFromContractedCompaniesFileByType,
            isGetIssuanceRenewalIdForStaffFromContractedCompaniesFileByTypeLoading,
        ],
    );

    return (
        <>
            <div className="bg-white z-10 p-2 md:p-4 rounded-xl flex flex-col gap-3 lg:gap-6 shadow-[0px_1px_5px_0px_rgba(113,112,163,0.3)]">
                <Accordion
                    id="1"
                    selectedKey={selectedKey}
                    onSelectionChange={setSelectedKey}
                    title={
                        <h1 className="text-xl md:text-2xl flex flex-1 -mt-2 items-center">
                            {t("2")}. {t("attachments")}
                        </h1>
                    }
                >
                    <div className="flex flex-col gap-2 lg:gap-4">
                        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2 lg:gap-4">
                            <FileUploadInput
                                name="uploadPersonalPhoto"
                                label={t("uploadPersonalPhoto")}
                                accept=".png,.jpg,.jpeg"
                                allowedTypes={allowedImagesTypes}
                                isAttachments={isAttachments?.havePersonalPhoto}
                                disabled={isFieldsDisabled}
                                control={control}
                                uploadRef={
                                    uploadRefs[
                                    ISSUANCE_RENEWAL_ID_ATTACHMENT_KEYS.PERSONAL_PHOTO
                                    ]
                                }
                                fileName={
                                    fileNames[
                                    ISSUANCE_RENEWAL_ID_ATTACHMENT_KEYS.PERSONAL_PHOTO
                                    ]
                                }
                                setFileName={(val) =>
                                    setFileName(
                                        ISSUANCE_RENEWAL_ID_ATTACHMENT_KEYS.PERSONAL_PHOTO,
                                        val,
                                    )
                                }
                                handleAttachmentView={handleAttachmentView}
                                selectedType={selectedType}
                                attachmentType={
                                    ISSUANCE_RENEWAL_ID_ATTACHMENT_TYPE.PERSONAL_PHOTO
                                }
                                isGetFileByTypeLoading={isGetFileByTypeLoading}
                                error={errors.uploadPersonalPhoto}
                                previewFiles={previewFiles}
                                setPreviewFiles={setPreviewFiles}
                                isRemovable={false}
                            />

                            <FileUploadInput
                                name="uploadCopyOfNationalId"
                                label={t("uploadCopyOfNationalId")}
                                accept=".png,.jpg,.jpeg,.pdf"
                                allowedTypes={allowedFileTypes}
                                isAttachments={isAttachments?.haveNationalIdCopy}
                                disabled={isFieldsDisabled}
                                control={control}
                                uploadRef={
                                    uploadRefs[
                                    ISSUANCE_RENEWAL_ID_ATTACHMENT_KEYS.NATIONAL_ID_COPY
                                    ]
                                }
                                fileName={
                                    fileNames[
                                    ISSUANCE_RENEWAL_ID_ATTACHMENT_KEYS.NATIONAL_ID_COPY

                                    ]
                                }
                                setFileName={(val) =>
                                    setFileName(
                                        ISSUANCE_RENEWAL_ID_ATTACHMENT_KEYS.NATIONAL_ID_COPY,
                                        val,
                                    )
                                }
                                handleAttachmentView={handleAttachmentView}
                                selectedType={selectedType}
                                attachmentType={
                                    ISSUANCE_RENEWAL_ID_ATTACHMENT_TYPE.NATIONAL_ID_COPY
                                }
                                isGetFileByTypeLoading={isGetFileByTypeLoading}
                                error={errors.uploadCopyOfNationalId}
                                previewFiles={previewFiles}
                                setPreviewFiles={setPreviewFiles}
                                isRemovable={false}
                            />

                            <FileUploadInput
                                name="uploadContractorsAuthorizationLetter"
                                label={t("uploadContractorsAuthorizationLetter")}
                                accept=".png,.jpg,.jpeg,.pdf"
                                allowedTypes={allowedFileTypes}
                                isAttachments={isAttachments?.haveContractorsAuthorizationLetter}
                                disabled={isFieldsDisabled}
                                control={control}
                                uploadRef={
                                    uploadRefs[
                                    ISSUANCE_RENEWAL_ID_ATTACHMENT_KEYS.CONTRACTORS_AUTHORIZATION_LETTER
                                    ]
                                }
                                fileName={
                                    fileNames[
                                    ISSUANCE_RENEWAL_ID_ATTACHMENT_KEYS.CONTRACTORS_AUTHORIZATION_LETTER
                                    ]
                                }
                                setFileName={(val) =>
                                    setFileName(
                                        ISSUANCE_RENEWAL_ID_ATTACHMENT_KEYS.CONTRACTORS_AUTHORIZATION_LETTER,
                                        val,
                                    )
                                }
                                handleAttachmentView={handleAttachmentView}
                                selectedType={selectedType}
                                attachmentType={
                                    ISSUANCE_RENEWAL_ID_ATTACHMENT_TYPE.CONTRACTORS_AUTHORIZATION_LETTER
                                }
                                isGetFileByTypeLoading={isGetFileByTypeLoading}
                                error={errors.uploadContractorsAuthorizationLetter}
                                previewFiles={previewFiles}
                                setPreviewFiles={setPreviewFiles}
                                isRemovable={false}
                            />
                        </div>
                    </div>
                </Accordion>
            </div>

            <ViewAttachmentModal
                attachment={imageSrc}
                contentType={contentType}
                isOpen={isViewAttachmentModalOpen}
                onOpenChange={() => {
                    setIsViewAttachmentModalOpen(false);
                    setImageSrc("");
                }}
            />
        </>
    )
}

export default Attachments
